/**
 * KYCVerifyPage — `/kyc/verify`.
 *
 * Slice 2: Operator review queue for citizen tier-upgrade requests.
 * Shows PENDING_REVIEW requests and allows approve/reject per request.
 *
 * Flow:
 *   1. List pending tier-upgrade requests (GET /v1/kyc/tier-upgrade-requests/pending).
 *   2. Click a request to see detail.
 *   3. Approve (POST /:id/approve) or Reject with reason (POST /:id/reject).
 */
import { type JSX, useState } from 'react';
import { CheckCircle, Clock, XCircle } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import {
  approveTierUpgrade,
  listPendingTierUpgrades,
  rejectTierUpgrade,
  type TierUpgradeRequest,
} from '../../lib/api/kyc';
import { PageHeader, StatusBadge } from '../shared/StaffUI';

function ErrorBanner({ title, message }: { readonly title: string; readonly message: string }): JSX.Element {
  return (
    <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900 space-y-1">
      <p className="font-semibold">{title}</p>
      <p className="text-red-800/90">{message}</p>
    </div>
  );
}

function tierLabel(tier: string): string {
  const map: Record<string, string> = {
    TIER_0: 'Tier 0 — Anonymous',
    TIER_1: 'Tier 1 — Basic',
    TIER_2: 'Tier 2 — Standard (NIN verified)',
    TIER_3: 'Tier 3 — Enhanced',
  };
  return map[tier] ?? tier;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function RequestDetail({
  request,
  onApprove,
  onReject,
  approving,
  rejecting,
}: {
  readonly request: TierUpgradeRequest;
  readonly onApprove: () => void;
  readonly onReject: (reason: string) => void;
  readonly approving: boolean;
  readonly rejecting: boolean;
}): JSX.Element {
  const [showReject, setShowReject] = useState(false);
  const [reason, setReason] = useState('');

  return (
    <div className="bg-white border border-border rounded-xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-gray-900">Request detail</h2>
        <StatusBadge status={request.status} />
      </div>

      <dl className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <dt className="text-xs text-muted">Citizen ID</dt>
          <dd className="font-mono text-xs mt-0.5">{request.citizenUserId}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted">Request ID</dt>
          <dd className="font-mono text-xs mt-0.5" data-testid="request-id">{request.id}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted">Current tier</dt>
          <dd className="text-xs mt-0.5">{tierLabel(request.currentTier)}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted">Target tier</dt>
          <dd className="text-xs mt-0.5">{tierLabel(request.targetTier)}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted">NIMC outcome</dt>
          <dd className="text-xs mt-0.5">
            {request.nimcOutcome === 'VERIFIED' ? (
              <span className="text-success">Verified</span>
            ) : (
              <span className="text-danger">Declined — manual review required</span>
            )}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted">Submitted</dt>
          <dd className="text-xs mt-0.5">{formatDate(request.createdAt)}</dd>
        </div>
      </dl>

      {request.status === 'PENDING_REVIEW' ? (
        <div className="flex items-center gap-3 pt-2 border-t border-border">
          <button
            type="button"
            data-testid="approve-btn"
            onClick={onApprove}
            disabled={approving || rejecting}
            className="px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            <CheckCircle className="w-4 h-4" />
            {approving ? 'Approving…' : 'Approve'}
          </button>

          {!showReject ? (
            <button
              type="button"
              data-testid="reject-btn"
              onClick={() => setShowReject(true)}
              disabled={approving || rejecting}
              className="px-4 py-2 border border-danger text-danger text-sm font-semibold rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              <XCircle className="w-4 h-4" />
              Reject
            </button>
          ) : (
            <div className="flex-1 flex items-center gap-2">
              <input
                type="text"
                data-testid="rejection-reason-input"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Rejection reason (required)"
                className="flex-1 px-3 py-2 border border-border rounded-lg text-sm"
              />
              <button
                type="button"
                data-testid="reject-confirm-btn"
                onClick={() => { onReject(reason); setShowReject(false); }}
                disabled={reason.trim().length === 0 || rejecting}
                className="px-4 py-2 bg-danger text-white text-sm font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {rejecting ? 'Rejecting…' : 'Confirm reject'}
              </button>
              <button
                type="button"
                onClick={() => setShowReject(false)}
                className="text-xs text-muted hover:text-gray-700"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      ) : (
        <p className="text-xs text-muted pt-2 border-t border-border">
          This request has already been resolved ({request.status}).
          {request.rejectionReason ? ` Reason: ${request.rejectionReason}` : null}
        </p>
      )}
    </div>
  );
}

export function KYCVerifyPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<TierUpgradeRequest | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const pending = useQuery({
    queryKey: ['kyc', 'tier-upgrade', 'pending'],
    queryFn: () => listPendingTierUpgrades({ pageSize: 50 }),
    retry: false,
    staleTime: 15_000,
  });

  const denied =
    pending.isError &&
    pending.error instanceof ApiError &&
    (pending.error.status === 401 || pending.error.status === 403);

  const approveMutation = useMutation({
    mutationFn: (id: string) => approveTierUpgrade(id),
    onSuccess: (updated) => {
      setSelected(updated);
      setActionError(null);
      void queryClient.invalidateQueries({ queryKey: ['kyc', 'tier-upgrade'] });
    },
    onError: (err) => {
      setActionError(err instanceof Error ? err.message : 'Approval failed');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      rejectTierUpgrade(id, reason),
    onSuccess: (updated) => {
      setSelected(updated);
      setActionError(null);
      void queryClient.invalidateQueries({ queryKey: ['kyc', 'tier-upgrade'] });
    },
    onError: (err) => {
      setActionError(err instanceof Error ? err.message : 'Rejection failed');
    },
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="KYC review queue"
        description="Tier-upgrade requests pending manual operator review."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: pending list */}
        <div className="lg:col-span-1 space-y-3">
          <h2 className="text-sm font-semibold text-gray-700">
            Pending
            {pending.data ? (
              <span className="ml-2 text-xs text-muted font-normal">
                ({pending.data.total} total)
              </span>
            ) : null}
          </h2>

          {denied ? (
            <ErrorBanner
              title="Permission required"
              message="Token missing Kyc/listPendingReview. Contact the platform admin to grant the role."
            />
          ) : pending.isPending ? (
            <p className="text-sm text-muted">Loading…</p>
          ) : pending.isError ? (
            <ErrorBanner title="Queue unavailable" message={(pending.error as Error).message} />
          ) : (pending.data?.items.length ?? 0) === 0 ? (
            <div
              className="bg-white border border-border rounded-xl p-6 text-center"
              data-testid="empty-queue"
            >
              <Clock className="w-8 h-8 text-muted mx-auto mb-2" />
              <p className="text-sm text-muted">No pending requests.</p>
            </div>
          ) : (
            <div
              className="space-y-2"
              data-testid="kyc-pending-list"
            >
              {pending.data?.items.map((req) => (
                <button
                  key={req.id}
                  type="button"
                  data-testid={`queue-item-${req.id}`}
                  onClick={() => { setSelected(req); setActionError(null); }}
                  className={`w-full text-left bg-white border rounded-xl p-4 hover:shadow-sm transition-shadow ${
                    selected?.id === req.id
                      ? 'border-nipost-green ring-2 ring-nipost-green/20'
                      : 'border-border'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-mono text-gray-500">
                      {req.id.slice(0, 8)}…
                    </span>
                    <StatusBadge status={req.status} />
                  </div>
                  <p className="text-xs text-gray-700">
                    {req.currentTier} → {req.targetTier}
                  </p>
                  <p className="text-[10px] text-muted mt-1">
                    {new Date(req.createdAt).toLocaleString()}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right: detail panel */}
        <div className="lg:col-span-2">
          {selected ? (
            <>
              {actionError ? (
                <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-danger">
                  {actionError}
                </div>
              ) : null}
              <RequestDetail
                request={selected}
                onApprove={() => approveMutation.mutate(selected.id)}
                onReject={(reason) => rejectMutation.mutate({ id: selected.id, reason })}
                approving={approveMutation.isPending}
                rejecting={rejectMutation.isPending}
              />
            </>
          ) : (
            <div className="bg-gray-50 border border-dashed border-border rounded-xl p-8 text-center">
              <p className="text-sm text-muted">Select a request from the queue to review.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
